import React, { useEffect, useState } from 'react';
import StockController from '../controllers/stock-controller';

/**
 * Popup that shows the real per-depot / per-variant stock for one product,
 * sourced from the Stocks table (not the denormalized Product.stock field).
 */
const StockBreakdownModal = ({ isOpen, onClose, productId, productName }) => {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !productId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await StockController.getByProducts([productId]);
        if (!cancelled) setLines(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError('Impossible de charger le stock.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, productId]);

  if (!isOpen) return null;

  // Group by variant (or 'no-variant')
  const groups = lines.reduce((acc, l) => {
    const key = l.productVariantId ?? 0;
    if (!acc[key]) {
      acc[key] = {
        productVariantId: l.productVariantId,
        variantName: l.variantName,
        variantReference: l.variantReference,
        rows: [],
        total: 0,
      };
    }
    acc[key].rows.push(l);
    acc[key].total += l.quantite || 0;
    return acc;
  }, {});
  const groupList = Object.values(groups);
  const grandTotal = lines.reduce((s, l) => s + (l.quantite || 0), 0);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
        <div className="fixed inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>

        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Stock par dépôt</h3>
              {productName && <p className="text-sm text-gray-500">{productName}</p>}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Chargement...</div>
            ) : error ? (
              <div className="text-center py-8 text-red-600">{error}</div>
            ) : lines.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Aucun stock enregistré pour ce produit.</div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between bg-[#f0f4f4] rounded-md px-4 py-2">
                  <span className="text-sm font-medium text-gray-700">Total tous dépôts</span>
                  <span className="px-3 py-1 text-sm font-semibold text-white bg-[#174540] rounded-full">{grandTotal}</span>
                </div>

                {groupList.map((g) => (
                  <div key={g.productVariantId ?? 'none'} className="border border-gray-200 rounded-md">
                    <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {g.variantName || 'Sans variante'}
                        </div>
                        {g.variantReference && (
                          <div className="text-xs text-gray-500">Réf: {g.variantReference}</div>
                        )}
                      </div>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${g.total > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {g.total}
                      </span>
                    </div>
                    <table className="min-w-full text-sm">
                      <thead className="text-xs uppercase text-gray-500">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium">Dépôt</th>
                          <th className="px-4 py-2 text-right font-medium">Quantité</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {g.rows.map((r) => (
                          <tr key={r.id}>
                            <td className="px-4 py-2 text-gray-700">{r.depotName || '—'}</td>
                            <td className="px-4 py-2 text-right text-gray-900 font-medium">{r.quantite || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-end rounded-b-lg">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockBreakdownModal;
